import _, { isEmpty } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";
import ReactMarkdown from "react-markdown";

import {
  renderMascot,
  conversationFeedBack,
  convertGifToPng,
  getNextOptions,
  makeNextOptions,
  formatMessageEdit,
  addOptionMsg,
  ALL_CHAT_SUBJECTS,
  linkifyText,
} from "utils";
import {
  addNewMessage,
  getActiveOptionComp,
  getArtieLogo,
  getChatbotTitleDetails,
  getIsMaximized,
  getShowFeedback,
  selectIsDisabled,
  selectIsFaq,
  selectLabelItems,
  selectOptionsTypeLogs,
  selectReplies,
  setActiveOptionComp,
  setLabelItems,
  setOptionsTypeLogs,
  setReplies,
  updateMessage,
  updateScroll,
  selectPromotionOffer,
  getButtonBgColor,
  setBadgeSwitchPhotos,
  setFocusedPhotoIdx,
  setGalleries,
  setIsPhotoTourScreen,
  setSelectedPhotoSwitchId,
} from "slices";
import { Icon, CustomTooltip, OptionsBox, PerformanceReport } from "components";
import UnitView from "components/OptionsBox/UnitView";
import GalleryView from "components/OptionsBox/GalleryView";
import VideosGalleryView from "components/OptionsBox/VideosGalleryView";
import FeaturesView, { Offer } from "components/OptionsBox/FeaturesView";
import HandleRangeSlider from "components/OptionsBox/HandleRangeSlider";
import { isMarkdown } from "utils/methods/index";
import ProgressiveImage from "components/ProgressiveImage/index";
import ExtraOption from "components/OptionsBox/ExtraOptions/index";
import FeeTransparency from "components/FeeTransparency";

import FeedbackForm from "./Feedback";
import ContactForm from "./ContactForm";
import FollowUp from "./FollowUp";
import Neighborhood from "./Neighborhood";
import { DoIQualify } from "./DoIQualify";
import AgentHandOff from "./AgentHandOff";
import { MessageTimeStamp } from "./MessageTimeStamp/index";
import ConfirmedTourForm from "./ConfirmedTourForm";
import EmbedMapView from "./EmbedMapView";

const Message = ({ data, mascotSettings, position, isMobile, index, loadMoreRef, childRef, isScrollableFaq, messagesContainerRef, messages }) => {
  const customSize = {
    width: "36",
    height: "36",
    sWidth: "40",
    sHeight: "16",
  };

  const [isHover, setIsHover] = useState(false);
  const [like, setLike] = useState(null);
  const [likeColor, setLikeColor] = useState(null);
  const [dislikeColor, setDislikeColor] = useState(null);
  const [staticImageData, setStaticImageData] = useState("");
  const color = useSelector(getButtonBgColor);
  const isMaximized = useSelector(getIsMaximized);
  const artieLogo = useSelector(getArtieLogo);

  const titleDetails = useSelector(getChatbotTitleDetails);
  const isFeedbackShow = useSelector(getShowFeedback);
  const optionsTypeLogs = useSelector(selectOptionsTypeLogs);
  const selectedReply = useSelector(selectReplies);
  const isOptionDisabled = useSelector(selectIsDisabled);
  const activeOptionComp = useSelector(getActiveOptionComp);
  const isFaq = useSelector(selectIsFaq);
  const prevLabel = useSelector(selectLabelItems);
  const promotionOffer = useSelector(selectPromotionOffer);

  const paramObj = { ...prevLabel };

  useEffect(() => {
    if (artieLogo?.includes?.("image/gif")) {
      convertGifToPng(artieLogo)
        .then((dataUrl) => {
          setStaticImageData(dataUrl);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [artieLogo]);

  const {
    speaker,
    message,
    type,
    feedback,
    id,
    optionsType,
    replies,
    key,
    isAlignRight,
    subject,
    available_tourTypes,
    createdAt,
    compData,
    follow_up,
    images,
    disableEdit,
    fee_utilities_summary,
    fee_summary_response,
  } = data;
  const dispatch = useDispatch();

  useEffect(() => {
    setLike(feedback || null);
  }, [feedback]);

  const handleFeedBack = (thumb) => {
    const checkThumb = like == thumb ? "" : thumb;

    dispatch(updateScroll(true));

    dispatch(
      updateMessage({
        id,
        feedback: checkThumb,
      })
    );

    setLike(checkThumb);
    conversationFeedBack(id, checkThumb);
  };

  const handleHover = useCallback(
    (type, isHovered) => {
      if (isMobile) {
        return;
      }

      if (isHovered) {
        if (type === "like") {
          setLikeColor("#26BBED");
        } else {
          setDislikeColor("#26BBED");
        }
      } else {
        if (type === "like") {
          setLikeColor(null);
        } else {
          setDislikeColor(null);
        }
      }
    },
    [isMobile]
  );

  const handleOptionSelect = () => {
    if (isOptionDisabled) {
      return;
    }

    dispatch(setActiveOptionComp(null));

    if (optionsType === "Init") {
      formatMessageEdit("Init");
    } else {
      getNextOptions(optionsType)
        .then((res) => {
          const { nextReplies, nextBotMessages } = makeNextOptions(optionsType, res.data, res.extraData || undefined);
          dispatch(addNewMessage(nextBotMessages));
          dispatch(setReplies(nextReplies));
          dispatch(setOptionsTypeLogs(optionsTypeLogs.slice(0, -1)));
        })
        .catch((err) => {
          /** @todo Add error handling part */
        });
    }
  };

  const messagesLength = messages.length;

  const isLastBot = index === messagesLength - 1;

  const isUserMsg = ["user", "faqUser"].includes(speaker);
  const compMsgClass = isMaximized ? "hayley-pl-12" : "";
  const compMsgMarginClass = `${compMsgClass} ${isLastBot && "!hayley-mb-[15%]"}`;
  const isComponent = ["customRange"].includes(activeOptionComp?.label);
  const isBoxView = ["boxView"].includes(selectedReply.renderType);
  const isMultiView = !isBoxView || ["AvailableTimes"].includes(selectedReply?.optionsType);
  const isShowFlex = messagesLength === 1;

  const getSubjectActions = (subject) => {
    if (!ALL_CHAT_SUBJECTS.includes(subject)) {
      return;
    }

    const components = {
      agent_handoff: (
        <AgentHandOff
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
          message={message}
          createdAt={createdAt}
        />
      ),
      "do i qualify": (
        <DoIQualify
          replies={selectedReply}
          available_tourTypes={available_tourTypes}
          subject={subject}
          message={message}
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
        />
      ),
      "positive feedback": (
        <FeedbackForm
          staticImageData={staticImageData}
          customSize={customSize}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          position={position}
          mascotSettings={mascotSettings}
          isPositive
        />
      ),
      "negative feedback": (
        <FeedbackForm
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
        />
      ),
      "contact us": (
        <ContactForm
          staticImageData={staticImageData}
          customSize={customSize}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          position={position}
          mascotSettings={mascotSettings}
        />
      ),
      "unsatisfactory answer": (
        <ContactForm
          unsatisfied
          staticImageData={staticImageData}
          customSize={customSize}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          position={position}
          mascotSettings={mascotSettings}
        />
      ),
      schedule: (
        <FollowUp
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
          follow_up={follow_up}
        />
      ),
      neighborhood: (
        <Neighborhood
          subject={subject}
          message={message}
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
          neighborhood={data?.neighborhood}
        />
      ),
      neighborhood_summary: (
        <Neighborhood
          summary={data.summary}
          subject={subject}
          message={message}
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
        />
      ),
      cancel_inquiry: (
        <ConfirmedTourForm
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
          message={message}
        />
      ),
      embed_map_view: (
        <EmbedMapView
          staticImageData={staticImageData}
          isLastBot={isLastBot}
          isUserMsg={isUserMsg}
          customSize={customSize}
          position={position}
          mascotSettings={mascotSettings}
          message={message}
        />
      ),
    };

    return components[subject];
  };

  const getCompView = () => {
    switch (key) {
      case "find_home":
        return (
          <div className={compMsgClass}>
            <UnitView showButtons={isLastBot} replies={replies} />
          </div>
        );

      case "photos":
        if (images) {
          return (
            <GalleryView
              showButtons={isLastBot}
              message={message}
              isUserMsg={isUserMsg}
              staticImageData={staticImageData}
              customSize={customSize}
              position={position}
              mascotSettings={mascotSettings}
              compMsgMarginClass={compMsgMarginClass}
              images={images}
            />
          );
        }

        return null;

      case "videos":
        return (
          <div className={compMsgMarginClass}>
            <VideosGalleryView showButtons={isLastBot} />
          </div>
        );

      case "community_info":
        return (
          <div className={compMsgMarginClass}>
            <FeaturesView showButtons={isLastBot} />
          </div>
        );

      case "report":
        return <PerformanceReport showButtons={isLastBot} data={compData} />;

      case "special_offer":
        return <Offer title={t("specialOffers")} data={promotionOffer} isSingleView showButtons={isLastBot} />;

      case "text":
        return (
          <div className={compMsgClass + "hayley-mb-[18px] hayley-ml-12 "}>
            <div className="hayley-text-sm" dangerouslySetInnerHTML={{ __html: message }} />
            <ExtraOption from="community_info" extraButtons={null} />
          </div>
        );

      default:
        break;
    }
  };

  const handleImageClick = (idx, d) => {
    let photos = { interior: images };
    dispatch(setGalleries(photos));

    dispatch(
      setBadgeSwitchPhotos(
        _(photos)
          .keys()
          .filter((k) => !_.isEmpty(photos[k]))
          .map((k) => ({ id: k.toLowerCase(), title: k }))
          .valueOf()
      )
    );

    dispatch(setSelectedPhotoSwitchId("interior"));
    dispatch(setFocusedPhotoIdx(idx));
    dispatch(setIsPhotoTourScreen(true));
  };

  const MarkdownOrHTMLRenderer = ({ content, message, isUserMsg, isMaximized }) => {
    if (!isMarkdown(content)) {
      // Convert URLs to clickable links
      const linkifiedContent = linkifyText(t(content));

      return (
        <div
          className={`${
            !message?.includes("Typing") &&
            !message?.includes("Loading") &&
            !message?.includes("Submitting") &&
            ["Form4ScheduleTour", "Form4ContactUs"].includes(optionsType)
              ? "hayley-whitespace-pre-line "
              : ""
          } hayley-mx-3 ${
            isMaximized ? "hayley-max-w-[725px]" : "hayley-max-w-[318px]"
          } hayley-rounded-lg !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary ${
            isUserMsg && !isMaximized ? "hayley-rounded-tr-none " : "hayley-rounded-tl-none "
          } ${isUserMsg ? "hayley-bg-custom-2" : "hayley-bg-custom-7"}`}
          style={{ wordWrap: "break-word !important", overflowWrap: "break-word" }}
          dangerouslySetInnerHTML={{ __html: linkifiedContent }}
        />
      );
    }

    return (
      <div
        className={`markdown-class ${
          !message.includes("Typing") &&
          !message.includes("Loading") &&
          !message.includes("Submitting") &&
          ["Form4ScheduleTour", "Form4ContactUs"].includes(optionsType)
            ? "hayley-whitespace-pre-line "
            : ""
        } hayley-mx-3 ${
          isMaximized ? "hayley-max-w-[725px]" : "hayley-max-w-[318px]"
        } hayley-rounded-lg !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary ${
          isUserMsg && !isMaximized ? "hayley-rounded-tr-none " : "hayley-rounded-tl-none "
        } ${isUserMsg ? "hayley-bg-custom-2" : "hayley-bg-custom-7"}`}
        style={{ wordWrap: "break-word !important", overflowWrap: "break-word" }}
      >
        <ReactMarkdown
          components={{
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" style={{ color: "#26BBED", textDecoration: "underline" }} />,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  return (
    <>
      {fee_utilities_summary && fee_summary_response ? (
        <div className={`${compMsgMarginClass} hayley-mb-3`}>
          <div className="hayley-flex hayley-items-start hayley-gap-3">
            {isEmpty(artieLogo) && <div className="hayley-h-9 hayley-w-9">{renderMascot(mascotSettings, position, true, customSize, false)}</div>}

            {!isEmpty(artieLogo) && (
              <div className="hayley-z-10 hayley-flex hayley-h-9 hayley-w-9 hayley-items-center hayley-justify-center hayley-rounded-full hayley-border hayley-border-[#EEE]">
                <img
                  id="staticImage"
                  src={artieLogo.includes("data:image/gif") ? staticImageData : artieLogo}
                  alt=""
                  className={`hayley-h-8 hayley-w-8 hayley-rounded-full hayley-object-cover`}
                />
              </div>
            )}

            <div className="hayley-flex-1">
              <MarkdownOrHTMLRenderer
                isMaximized={isMaximized}
                message={message}
                isUserMessage={isUserMsg}
                content={message?.replace("Hayley", titleDetails.chatbot_title || "Hayley")}
              />
              <FeeTransparency
                totalPrice={null}
                baseRent={null}
                leaseTerm={null}
                fees={fee_summary_response}
                unitBedrooms={null}
                unitArea={null}
                availabilityDate={null}
                hidePricing={false}
                tmlpEnabled={false}
                autoExpand={true}
              />
              {createdAt && <MessageTimeStamp time={createdAt} isUserMsg={false} isMaximized={isMaximized} messagesContainerRef={messagesContainerRef} />}
            </div>
          </div>
        </div>
      ) : speaker === "comp" ? (
        getCompView()
      ) : !subject || !ALL_CHAT_SUBJECTS.includes(subject) ? (
        <div
          className={`${
            isFaq && isLastBot ? "!hayley-mb-[15%]" : !["unitView"].includes(selectedReply?.renderType) && isLastBot && !isComponent && "!hayley-mb-[25%]"
          }`}
        >
          {/* ${selectedReply.optionsType === "customButton" ? "hayley-flex-col" : ""} */}
          <div
            className={`${!subject ? "hayley-flex" : undefined}  hayley-flex-wrap ${
              isUserMsg && !isMaximized ? `hayley-justify-end ${isAlignRight && "hayley-text-right"}` : ``
            }`}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            <div className={`hayley-flex ${isLastBot && (isMaximized ? "hayley-mb-4" : "hayley-mb-3")}`}>
              {isUserMsg ? (
                isMaximized ? (
                  <Icon iconType="DefaultUser" classes={"hayley-h-9 hayley-w-9 hayley-object-cover"} />
                ) : (
                  ""
                )
              ) : (
                <>
                  {isEmpty(artieLogo) && <div className="hayley-h-9 hayley-w-9">{renderMascot(mascotSettings, position, true, customSize, false)}</div>}

                  {!isEmpty(artieLogo) && (
                    <div className="hayley-z-10 hayley-flex hayley-h-9 hayley-w-9 hayley-items-center hayley-justify-center hayley-rounded-full hayley-border hayley-border-[#EEE]">
                      <img
                        id="staticImage"
                        src={artieLogo.includes("data:image/gif") ? staticImageData : artieLogo}
                        alt=""
                        className={`hayley-h-8 hayley-w-8 hayley-rounded-full hayley-object-cover`}
                      />
                    </div>
                  )}
                </>
              )}

              <div>
                <div className="hayley-flex">
                  <div
                    className={`hayley-flex hayley-items-center ${speaker == "user" && isMaximized && "hayley-flex-row-reverse"}`}
                    ref={id === messages[4]?.id ? loadMoreRef : isScrollableFaq ? childRef : null}
                  >
                    {optionsType && !message.includes("Please explore our floor plans") && disableEdit !== true && (
                      <CustomTooltip title={t("reselect")} placement="bottom">
                        <div
                          className="hayley-mb-4 hayley-flex hayley-cursor-pointer hayley-items-center hayley-text-custom-4 hover:hayley-text-secondary"
                          onClick={handleOptionSelect}
                        >
                          <Icon iconType="Pencil" className="hayley-h-6 hayley-w-6 hayley-text-inherit" />
                        </div>
                      </CustomTooltip>
                    )}
                    <div>
                      <MarkdownOrHTMLRenderer
                        isMaximized={isMaximized}
                        message={message}
                        isUserMessage={isUserMsg}
                        content={message?.replace("Hayley", titleDetails.chatbot_title || "Hayley")}
                      />
                      {createdAt && (
                        <MessageTimeStamp time={createdAt} isUserMsg={isUserMsg} isMaximized={isMaximized} messagesContainerRef={messagesContainerRef} />
                      )}
                    </div>
                  </div>
                  {speaker == "faqBot" && isFeedbackShow && (
                    <div
                      className={`hayley-visible -hayley-mt-3 hayley-ml-1 hayley-flex hayley-items-center ${
                        isMobile || ((isHover || like) && speaker == "faqBot") ? "hayley-visible" : "hayley-invisible"
                      }`}
                    >
                      <CustomTooltip title={"This is helpful"} placement="top">
                        <div
                          className="hayley-cursor-pointer hayley-align-middle"
                          onClick={() => handleFeedBack("thumbs_up")}
                          onMouseEnter={() => handleHover("like", true)}
                          onMouseLeave={() => handleHover("like")}
                        >
                          <Icon iconType="like" color={likeColor || like == "thumbs_up" ? "#26BBED" : "#939393"} />
                        </div>
                      </CustomTooltip>
                      <div className="hayley-mx-2 -hayley-mt-2 hayley-h-4 hayley-w-[1px] hayley-bg-custom-6" />
                      <CustomTooltip title={"This is not helpful"} placement="top">
                        <div
                          className="hayley-cursor-pointer hayley-align-middle"
                          onClick={() => handleFeedBack("thumbs_down")}
                          onMouseEnter={() => handleHover("unlike", true)}
                          onMouseLeave={() => handleHover("unlike")}
                        >
                          <Icon iconType="dislike" color={dislikeColor || like == "thumbs_down" ? "#26BBED" : "#939393"} />
                        </div>
                      </CustomTooltip>
                    </div>
                  )}
                </div>

                {images && images.length > 0 && (
                  <div
                    className={`hayley-grid ${images.length === 1 ? "hayley-grid-cols-1" : "hayley-grid-cols-2"} 
                           hayley-mt-3
                          hayley-w-fit hayley-gap-[8px] hayley-pl-[12px]`}
                  >
                    {images?.map((img, idx) => {
                      return (
                        <ProgressiveImage
                          key={img.id + idx}
                          classes={{
                            root: `${
                              images.length === 1 ? "hayley-h-[240px] hayley-w-[422px]" : "hayley-h-[130px] hayley-w-[207px]"
                            } hayley-rounded-[4px]   hayley-overflow-hidden`,
                            img: "hayley-h-full hayley-w-full hayley-cursor-pointer  hayley-object-center hayley-rounded-[4px]  hayley-border-0",
                          }}
                          wrapperProps={{
                            onClick: () => handleImageClick(idx, img),
                            sx: { "&.hayley-custom:hover": { borderColor: `${color} !important` } },
                          }}
                          src={img?.image_url}
                          alt=""
                          selectedId={""}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {isMaximized && !isMultiView && isLastBot && !isShowFlex ? (
              <OptionsBox
                messagesContainerRef={messagesContainerRef}
                messagesLength={messagesLength}
                isMessage={!subject || !ALL_CHAT_SUBJECTS.includes(subject)}
              />
            ) : null}
          </div>

          {(!isMaximized || (isShowFlex && isMaximized) || (isMultiView && isMaximized)) && isLastBot ? (
            <OptionsBox messagesLength={messagesLength} messagesContainerRef={messagesContainerRef} />
          ) : null}
          {isComponent && isLastBot ? (
            <div className={`hayley-mb-[25%] ${isMaximized ? "hayley-pl-12" : "hayley-mt-3"}`}>
              <HandleRangeSlider
                activeOptionComp={activeOptionComp}
                handleClick={(newMessage, callbackOptions) => {
                  const { data } = callbackOptions;

                  paramObj["price"] = data.data;
                  dispatch(setLabelItems(paramObj));

                  addOptionMsg(prevLabel?.desired_move_in ? "PriceBrackets" : "WhenMoveIn", newMessage);
                }}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div>
          {getSubjectActions(subject) ? (
            getSubjectActions(subject)
          ) : isLastBot ? (
            <OptionsBox
              type={type}
              messagesLength={messagesLength}
              subject={subject}
              available_tourTypes={available_tourTypes}
              staticImageData={staticImageData}
              customSize={customSize}
              isLastBot={isLastBot}
              isUserMsg={isUserMsg}
              position={position}
              mascotSettings={mascotSettings}
              messagesContainerRef={messagesContainerRef}
            />
          ) : null}
        </div>
      )}
    </>
  );
};

export default Message;
